import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Empty,
  Spin,
  Typography,
  Row,
  Col,
  Statistic,
  Button,
  Tooltip,
  Badge,
  message,
  DatePicker,
  Collapse,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  CopyOutlined,
  FilterOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { EXECUTOR_API } from '../types';
import { buildAuthHeaders } from '../utils/userStore';
import { Dayjs } from 'dayjs';
import ExecutionDetailDrawer, { ExecutionDetail } from './ExecutionDetailDrawer';

const { Text } = Typography;

interface Execution {
  run_id: string;
  status: 'success' | 'error';
  started_at: string;
  finished_at: string;
  duration_ms: number;
}

interface Props {
  agentId: string | null;
}

const ExecutionHistory: React.FC<Props> = ({ agentId }) => {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<ExecutionDetail | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const fetchExecutions = useCallback(async () => {
    if (!agentId) return;

    setLoading(true);
    try {
      let url = `${EXECUTOR_API}/agent/builder/${agentId}/executions?limit=10&offset=0`;

      if (startDate) {
        url += `&startDate=${startDate.toISOString()}`;
      }
      if (endDate) {
        url += `&endDate=${endDate.toISOString()}`;
      }

      const res = await fetch(url, { headers: buildAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setExecutions(data.executions || []);
      }
    } catch (error) {
      console.error('Failed to fetch executions', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, startDate, endDate]);

  const fetchExecutionDetail = useCallback(
    async (runId: string) => {
      if (!agentId) return;

      try {
        const res = await fetch(`${EXECUTOR_API}/agent/builder/${agentId}/executions/${runId}`, {
          headers: buildAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setSelectedExecution(data);
          setDrawerOpen(true);
        } else {
          message.error('No se pudo cargar los detalles de la ejecución');
        }
      } catch (error) {
        message.error('Error al cargar detalles');
        console.error(error);
      }
    },
    [agentId]
  );

  // Initial fetch
  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchExecutions();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchExecutions]);

  if (!agentId) {
    return (
      <Card>
        <Empty description="Crea un agente para ver las ejecuciones" />
      </Card>
    );
  }

  const columns = [
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag
          icon={status === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          color={status === 'success' ? 'green' : 'red'}
        >
          {status === 'success' ? 'Éxito' : 'Error'}
        </Tag>
      ),
    },
    {
      title: 'Duración',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      width: 100,
      render: (ms: number) => <Text>{(ms / 1000).toFixed(2)}s</Text>,
      sorter: (a: Execution, b: Execution) => a.duration_ms - b.duration_ms,
    },
    {
      title: 'Inicio',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 180,
      render: (date: string) => {
        const d = new Date(date);
        return (
          <Tooltip title={d.toLocaleString('es-ES')}>
            <Text type="secondary">{d.toLocaleTimeString('es-ES')}</Text>
          </Tooltip>
        );
      },
      sorter: (a: Execution, b: Execution) =>
        new Date(a.started_at).getTime() - new Date(b.started_at).getTime(),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: Execution) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => fetchExecutionDetail(record.run_id)}
          >
            Detalles
          </Button>
          <Button
            type="link"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(record.run_id);
              message.success('run_id copiado');
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <Badge
              count={executions.length}
              color="#1677ff"
              style={{ boxShadow: 'none' }}
            />
            <span>Historial de Ejecuciones</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              type={autoRefresh ? 'primary' : 'default'}
              size="small"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              {autoRefresh ? 'Auto' : 'Manual'}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={() => fetchExecutions()}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
        }
      >
        {/* Date Filters */}
        <Collapse
          items={[
            {
              key: '1',
              label: (
                <Space>
                  <FilterOutlined />
                  <span>Filtrar por fecha</span>
                  {(startDate || endDate) && (
                    <Badge color="#1677ff" text="Activo" />
                  )}
                </Space>
              ),
              children: (
                <div style={{ marginBottom: 24 }}>
                  <Row gutter={16} align="middle">
                    <Col xs={24} sm={8}>
                      <div style={{ fontSize: 12, marginBottom: 4 }}>
                        <Text type="secondary">Desde</Text>
                      </div>
                      <DatePicker
                        value={startDate}
                        onChange={(date) => {
                          setStartDate(date);
                          fetchExecutions();
                        }}
                        placeholder="Fecha inicial"
                        style={{ width: '100%' }}
                        showTime
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <div style={{ fontSize: 12, marginBottom: 4 }}>
                        <Text type="secondary">Hasta</Text>
                      </div>
                      <DatePicker
                        value={endDate}
                        onChange={(date) => {
                          setEndDate(date);
                          fetchExecutions();
                        }}
                        placeholder="Fecha final"
                        style={{ width: '100%' }}
                        showTime
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <Button
                        icon={<ClearOutlined />}
                        onClick={() => {
                          setStartDate(null);
                          setEndDate(null);
                          fetchExecutions();
                        }}
                        type="dashed"
                        block
                        size="small"
                        disabled={!startDate && !endDate}
                      >
                        Limpiar filtros
                      </Button>
                    </Col>
                  </Row>
                </div>
              ),
            },
          ]}
          style={{ marginBottom: 24 }}
        />
        {executions.length > 0 && (
          <Row gutter={24} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Statistic
                title="Total"
                value={executions.length}
                prefix={<Badge count={executions.length} color="#1677ff" />}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Exitosas"
                value={executions.filter((e) => e.status === 'success').length}
                suffix={`/ ${executions.length}`}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Tiempo Promedio"
                value={
                  (
                    executions.reduce((sum, e) => sum + e.duration_ms, 0) /
                    executions.length /
                    1000
                  ).toFixed(2)
                }
                suffix="s"
                valueStyle={{ color: '#1677ff' }}
              />
            </Col>
          </Row>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin />
          </div>
        ) : executions.length === 0 ? (
          <Empty
            description="Sin ejecuciones"
            style={{ padding: '40px 0' }}
          >
            <Text type="secondary">
              Las ejecuciones aparecerán aquí cuando se llame al webhook
            </Text>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={executions.map((e) => ({
              ...e,
              key: e.run_id,
            }))}
            pagination={{
              pageSize: 10,
              total: executions.length,
              showQuickJumper: false,
            }}
            size="small"
          />
        )}
      </Card>

      <ExecutionDetailDrawer
        detail={selectedExecution}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedExecution(null); }}
      />
    </>
  );
};

export default ExecutionHistory;
